import { useMemo } from 'react';

import { BalanceType } from '@pp/core/src/api/fetchers/userInfo/types';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { roundNumber } from '@pp/core/src/utils/number';

import { useHighflyerStateSlice } from '../../store/slices/highflyerState/selectors';

/**
 * Computes the balance and bonus based on the given parameters.
 *
 * @param {BalanceType} balance - The current balance of the user.
 * @param {number} totalBet - The total bet amount.
 * @param {number} digitsNumber - The number of digits to round the balance and bonus to.
 * @param {number} [winResult] - The win result amount. Optional.
 *
 * @returns {BalanceType} - The new balance and bonus.
 */
export const computeBalance = (
  balance: BalanceType,
  totalBet: number,
  digitsNumber: number,
  winResult?: number
): BalanceType => {
  const balanceDiff = balance.amount - totalBet;
  const bonusDiff = Math.min(balanceDiff, 0);
  const newBonus = balance.bonus + bonusDiff;
  let newBalance = Math.max(balanceDiff, 0);
  if (winResult) {
    newBalance += winResult;
  }
  return {
    ...balance,
    amount: roundNumber(newBalance, digitsNumber),
    bonus: roundNumber(newBonus, digitsNumber),
  };
};

/**
 * useCompositeBalance - get correct balance for UI to display
 *
 * does not update the value during active game rounds or when bets are closed or not allowed
 *
 * does update balance when making bets or win result balance request is taking too long
 */
export const useCompositeBalance = (): BalanceType | undefined => {
  const balance = useGameStateSlice.balance();
  const digitsNumber = useGameStateSlice.getCurrencyInfo()?.digitsNumber ?? 0;
  const betTimestamp = useBetSlice.getUpdateTimestamp();
  const hasAnyEarlyBets = useHighflyerStateSlice.hasAnyEarlyBets();
  const earlyBetAmount = useHighflyerStateSlice.getTotalEarlyBetAmount();
  const isLoadedInActiveRound = useGameStateSlice.isLoadedInActiveRound();
  const totalBet = useBetSlice.totalBet();
  const winResult = useGameStateSlice.winResult();

  return useMemo(() => {
    if (!balance) return;

    const balanceTimestamp = balance.updateTimestamp;
    const winResultTimestamp = winResult?.updateTimestamp ?? 0;

    //EarlyBets scenario
    if (earlyBetAmount > 0) {
      return computeBalance(balance, earlyBetAmount, digitsNumber);
    }
    //balance remains unchanged while the round is in dealing phase
    else if (balanceTimestamp > betTimestamp || (winResult && winResultTimestamp < balanceTimestamp)) {
      return balance;
    }

    return computeBalance(
      balance,
      totalBet,
      digitsNumber,
      /**
       * If the win result is the freshest of actions that affect balance - add it to the new balance amount (without subtracting totalBet).
       */
      winResult && winResultTimestamp > balanceTimestamp ? winResult.amount : undefined
    );
  }, [
    balance,
    betTimestamp,
    digitsNumber,
    isLoadedInActiveRound,
    totalBet,
    winResult,
    hasAnyEarlyBets,
    earlyBetAmount,
  ]);
};
